/**
 * Payment Solution Finder capture form — /payment-finder. Shown under the
 * quiz recommendation; one form replaces the old inline form and the
 * "email me the results" button.
 *
 * The quiz answers (as the visitor saw them) and the recommendation arrive as
 * `context`, built by paymentFinderContext below. The raw answer codes travel
 * alongside as `quiz_answers` so contextToCrm can map them without depending
 * on display copy.
 */
import { company, email, fullName, phone, TURNOVER_BANDS } from "./common";

/** Legacy analytics name kept so quote_requested / email_capture reports continue. */

const RECOMMENDATION = "Recommendation";
const BUSINESS_TYPE = "Business type";

/** Readable label per quiz question, in the order they're asked. */
const QUESTION_LABELS = {
  businessType: BUSINESS_TYPE,
  paymentMethod: "Takes payments",
  primaryInterest: "Most interested in",
  locations: "Number of locations",
  turnover: "Monthly card turnover",
  integrations: "Integrations needed",
  nextStep: "Wants next",
};

/** paymentMethod → How_do_you_take_payments_now ("exploring" → nothing). */
const PAYMENT_METHOD_CRM = {
  in_person: ["Card machine"],
  online: ["Online payments"],
  both: ["Card machine", "Online payments"],
};

/** primaryInterest → Needs (lower_costs / partner have no Needs value). */
const INTEREST_NEEDS = {
  card_machines: ["Card machine"],
  epos: ["EPOS"],
  gateway: ["Online payments"],
  payment_links: ["Pay by link"],
};

const LOCATION_VALUES = ["1", "2-3", "4-10", "10+"];

/**
 * `answers` are the raw quiz answers, `questions` the quiz's question list
 * ({ id, options: [{ value, label }] }), `result` { category } and `headline`
 * the recommendation title shown to the visitor.
 */
export function paymentFinderContext(answers, questions, result, headline) {
  if (!result) return {};
  const ctx = {};
  questions.forEach((q) => {
    const option = q.options.find((o) => o.value === answers[q.id]);
    if (option) ctx[QUESTION_LABELS[q.id] || q.label] = option.label;
  });
  ctx[RECOMMENDATION] = headline;
  ctx.recommendation_category = result.category;
  ctx.quiz_answers = { ...answers };
  return ctx;
}

const quizPaymentFinder = {
  id: "quiz_payment_finder",
  label: "Payment Solution Finder",
  crm: {
    layout: "Standard",
    leadSource: "Website Quiz - Payment Solution Finder",
    tags: (values, ctx) => (ctx.quiz_answers?.primaryInterest === "partner" ? ["Partner interest"] : []),
  },
  fields: [
    [fullName, company],
    [email, { ...phone, help: "We'll only call to discuss your results — never spam." }],
  ],
  submitLabel: "Send my recommendation",
  consent: { variant: "standard" },
  success: { title: "Thank you", body: "We'll be in touch with your personalised recommendation shortly." },
  contextToCrm: (ctx) => {
    const a = ctx.quiz_answers || {};
    const band = TURNOVER_BANDS.find((b) => b.value === a.turnover);
    return {
      IndustrySector: a.businessType && a.businessType !== "other" ? ctx[BUSINESS_TYPE] : null,
      How_do_you_take_payments_now: PAYMENT_METHOD_CRM[a.paymentMethod] || [],
      Needs: INTEREST_NEEDS[a.primaryInterest] || [],
      Number_of_Locations: LOCATION_VALUES.includes(a.locations) ? a.locations : null,
      Monthly_Turnover: band ? band.midpoint : null,
    };
  },
  summary: (values, ctx) => (ctx[RECOMMENDATION] ? `Payment Finder: ${ctx[RECOMMENDATION]}` : "Payment Finder"),
};

export default quizPaymentFinder;
